// src/config/env.ts

type NodeEnv = 'development' | 'production' | 'test';

export const ACCESS_TOKEN_KEY = 'psaumesetplantes_access_token';

/**
 * ============================================
 * 🌍 GLOBAL CONFIG
 * ============================================
 */
export const env = {
  NODE_ENV: (process.env.NODE_ENV as NodeEnv) || 'development',
  PORT: 3000,
  APP_NAME: 'psaumesetplantes' as const,
  APP_URL: 'https://www.psaumesetplantes.com' as const,


  API_BASE_URL: 'https://api.psaumesetplantes.com/api/v1' as const,
  IMAGE_BASE_URL: 'https://api.psaumesetplantes.com/storage/' as const,
  IMAGE_BASE_URL_SANS_STORAGE: 'https://api.psaumesetplantes.com/' as const,

  // API_BASE_URL: 'http://localhost:4000/api/v1' as const,
  // IMAGE_BASE_URL: 'http://localhost:4000/storage/' as const,
  // IMAGE_BASE_URL_SANS_STORAGE: 'http://localhost:4000/' as const,

  // API_BASE_URL: ' http://192.168.214.96:4000/api/v1' as const,
  // IMAGE_BASE_URL: ' http://192.168.214.96:4000/storage/' as const,
  // IMAGE_BASE_URL_SANS_STORAGE: ' http://192.168.214.96:4000/' as const,

  /**
   * ============================================
   * 🔐 AUTH
   * ============================================
   */
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_CODE: '/auth/verify-code',
    LOGOUT: '/logout',
  },

  /**
   * ============================================
   * 👤 USER
   * ============================================
   */
  USER: {
    INFOS: '/user/infos',
    UPDATE_PROFILE_PHOTO: '/user/update-profile-photo',
    DELETE_PROFILE_PHOTO: '/user/delete-profile-photo',
    UPDATE_PERSONAL_INFO: '/user/update-personal-info',
    UPDATE_PASSWORD: '/user/update-password',
  },

  /**
   * ============================================
   * ⚙️ SETTINGS
   * ============================================
   */
  SETTINGS: {
    PUBLIC: '/public/settings',
    ADMIN: '/admin/settings',
    UPDATE: '/admin/settings/updata',
    MEDIA: '/admin/settings/media',
  },

  /**
   * ============================================
   * 🧩 SERVICES
   * ============================================
   */
  SERVICES: {
    PUBLIC: '/public/services',
    ADMIN: '/admin/services',
  },

  /**
   * ============================================
   * 🛒 PRODUCTS
   * ============================================
   */
  PRODUCTS: {
    ADMIN: {
      BASE: '/admin/products',
      THUMBNAIL: (id: string) => `/admin/products/${id}/thumbnail`,
      GALLERY: (id: string) => `/admin/products/${id}/gallery`,
      GALLERY_DELETE: (id: string, index: number) =>
        `/admin/products/${id}/gallery/${index}`,
    },

    PUBLIC: {
      BASE: '/public/products',
      ONE: (id: string) => `/public/products/${id}`,
    },
  },

  // Ressources
  ADMIN_PRODUCT_RESOURCES_URL: (id: string) => `/admin/rs/${id}/resources`,
  ADMIN_PRODUCT_RESOURCE_DELETE_URL: (id: string, resourceId: string) => `/admin/rs/${id}/resources/${resourceId}`,


  /**
   * ============================================
   * 🧩 PRODUCT CATEGORIES
   * ============================================
   */
  PRODUCT_CATEGORIES: {
    ADMIN: '/admin/products-categories',
    PUBLIC: '/public/product-categories',
  },

  /**
   * ============================================
   * 🧩 PROJECT CATEGORIES
   * ============================================
   */
  PROJECT_CATEGORIES: {
    ADMIN: '/admin/project-categories',
  },

  /**
   * ============================================
   * 📝 ARTICLES
   * ============================================
   */
  ARTICLES: {
    ADMIN: {
      BASE: '/admin/articles',
      THUMBNAIL: (id: string) => `/admin/articles/${id}/thumbnail`,
      GALLERY: (id: string) => `/admin/articles/${id}/gallery`,
      GALLERY_DELETE: (id: string, index: number) =>
        `/admin/articles/${id}/gallery/${index}`,

      PUBLISH: (id: string) => `/admin/articles/${id}/publish`,
      UNPUBLISH: (id: string) => `/admin/articles/${id}/unpublish`,
    },
    PUBLIC: {
      BASE: "/public/articles",
      ONE: (id: string) => `/public/articles/${id}`,
    },
  },

  /**
   * ============================================
   * 🧩 ARTICLE CATEGORIES
   * ============================================
   */
  ARTICLE_CATEGORIES: {
    ADMIN: {
      BASE: '/admin/article-categories',
      IMAGE: (id: string) =>
        `/admin/article-categories/${id}/image`,
    },
  },

  /**
   * ============================================
   * 📩 CONTACT
   * ============================================
   */
  CONTACT: {
    PUBLIC: '/public/contact',
  },

  /**
   * ============================================
   * 👤 ADMIN USERS
   * ============================================
   */
  USERS: {
    ADMIN: {
      BASE: '/admin/users',

      BAN: (email: string) =>
        `/admin/users/${encodeURIComponent(email)}/ban`,

      UNBAN: (email: string) =>
        `/admin/users/${encodeURIComponent(email)}/unban`,

      ADD_ROLE: (email: string) =>
        `/admin/users/${encodeURIComponent(email)}/roles/add`,

      REMOVE_ROLE: (email: string) =>
        `/admin/users/${encodeURIComponent(email)}/roles/remove`,
    },
  },

  /**
   * ============================================
   * 🎭 ROLES
   * ============================================
   */
  ROLES: {
    ADMIN: '/admin/roles',
  },

  ADMIN_TOUR_KEY: process.env.REACT_APP_ADMIN_TOUR_KEY || '',


  /**
   * ============================================
   * 🛒 ORDERS (USER)
   * ============================================
   */
  ORDERS: {
    CREATE: '/user/orders',
    MY_ORDERS: '/user/orders/my-orders',
    LATEST_ORDER: '/user/orders/latest',
    SHOW: (id: string) => `/user/orders/${id}`,
  },

  /**
   * ============================================
   * 💳 PAYMENTS (USER)
   * ============================================
   */
  PAYMENTS: {
    INITIATE: '/user/order/payment/initiate',
    CHECK_TRANSACTION: (transactionId: string) => `/user/order/payment/check-transaction/${transactionId}`,
  },

  /**
   * ============================================
   * 👨‍💼 ADMIN ORDERS
   * ============================================
   */
  ADMIN_ORDERS: {
    BASE: '/admin/orders',
    SHOW: (id: string) => `/admin/orders/${id}`,
    UPDATE_STATUS: (id: string) => `/admin/orders/${id}/status`,
    DELETE: (id: string) => `/admin/orders/${id}`,
    SEND_WHATSAPP: (id: string) => `/admin/orders/${id}/send-whatsapp`,
  },

  /**
   * ============================================
   * 📊 ADMIN DASHBOARD
   * ============================================
   */
  ADMIN_DASHBOARD: '/admin/dashboard',

  /**
   * ============================================
   * 📈 ADMIN PRODUCT STATS
   * ============================================
   */
  ADMIN_PRODUCT_STATS: {
    OVERVIEW: '/admin/products/stats/overview',
    TOP: '/admin/products/stats/top',
    CATEGORIES: '/admin/products/stats/categories',
  },

  /**
   * ============================================
   * 👥 ADMIN CUSTOMERS
   * ============================================
   */
  ADMIN_CUSTOMERS: {
    BASE: '/admin/customers',
    SHOW: (id: string) => `/admin/customers/${id}`,
  },

  /**
   * ============================================
   * 💳 ADMIN TRANSACTIONS
   * ============================================
   */
  ADMIN_TRANSACTIONS: {
    BASE: '/admin/transactions',
    SHOW: (id: string) => `/admin/transactions/${id}`,
  },

  /**
   * ============================================
   * 📄 ADMIN REPORTS
   * ============================================
   */
  ADMIN_REPORTS: {
    ORDERS: '/admin/reports/orders',
    SALES: '/admin/reports/sales',
    EXPORT_CSV: '/admin/reports/export/csv',
    EXPORT_PDF: '/admin/reports/export/pdf',
  },



  /**
   * ============================================
   * 🛒 USER PURCHASES
   * ============================================
   */
  USER_PURCHASES: {
    DASHBOARD: '/user/purchases/dashboard',
    DIGITAL: '/user/purchases/digital',
    PHYSICAL: '/user/purchases/physical',
    TRANSACTIONS: '/user/purchases/transactions',
    PRODUCT_RESOURCES: (productId: string) => `/user/purchases/product/${productId}/resources`,
    DOWNLOAD_RESOURCE: (productId: string, resourceId: string) =>
      `/user/purchases/product/${productId}/resource/${resourceId}/download`,
  },



  /**
   * ============================================
   * 🔁 REDIRECT
   * ============================================
   */
  DEFAULT_REDIRECT_URL: 'https://www.psaumesetplantes.com' as const,
};